import React, { useState } from "react";
import { InfoDialog } from "components/shared-ui/dialogs/common/info-dialog/info-dialog";
import { LinkButton } from "components/shared-ui/linkButton/linkButton";
import Table, { IColumnDefinition } from "components/shared-ui/table/table";
import styles from "./import-bzs-result-dialog.module.scss";
import { IImportValidationStatus, IImportValidationSuccessStatus, ImportEntityType, importEntityTypeDict, ImportError } from "api/entities/import-bzs";
import { ImportBzsEntityComparisonDialog } from "../import-bzs-entity-comparison-dialog/import-bzs-entity-comparison-dialog";

export interface IImportBzsResultDialogProps {
    isOpen: boolean;
    importResult: IImportValidationSuccessStatus | IImportValidationStatus[];
    onClose: () => void;
}

export function ImportBzsResultDialog(props: IImportBzsResultDialogProps) {
    const hasErrors = !props.importResult
        || (Array.isArray(props.importResult) &&
            (props.importResult.some(x => !x.status)
            || props.importResult.length === 0));

    const isImportSuccessful = !!props.importResult && !Array.isArray(props.importResult) && props.importResult.status;

    const [isEntityComparisonDialogOpen, setIsEntityComparisonDialogOpen] = useState<boolean>(false);
    const [selectedImportValidationStatusItem, setSelectedImportValidationStatusItem] = useState<IImportValidationStatus | IImportValidationSuccessStatus>(null);

    const columns: IColumnDefinition<ImportError, string>[] = [
        {
            label: "Entity",
            className: styles["col--property"],
            formatter: row => importEntityTypeDict[row.entityType]
        },
        {
            label: "Error",
            className: styles["col--error-message"],
            formatter: row => row.message
        },
        {
            label: "Details",
            className: styles["col--details"],
            formatter: row =>
                row.entityType !== ImportEntityType.AccessCredential
                    && !!row.conflictingEntities
                    && Array.isArray(row.conflictingEntities)
                ?
                <LinkButton
                    testId="conflicting-entities-link"
                    onClick={() => {
                        setSelectedImportValidationStatusItem(row);
                        setIsEntityComparisonDialogOpen(true);
                    }}
                >
                    {row.conflictingEntities.length === 1
                        ? '1 entity'
                        : `${row.conflictingEntities.length} entities`}
                </LinkButton>
                    : <></>
        }
    ];

    let importErrors: ImportError[] = Array.isArray(props.importResult)
        ? props.importResult
            .filter(x => !x.status)
            .map((x, i) => ({
                id: i + 1,
                ...x
            }))
        : [];

    return (
        <>
            <InfoDialog
                testId='import-results-dialog'
                title={hasErrors ? 'Import bZs configuration failed': 'Import succeeded'}
                showOkButton={true}
                open={props.isOpen}
                onClose={() => {
                    props.onClose();
                }}
            >
                <div className={styles.content}>
                    {!hasErrors &&
                        <>
                            <div>
                                bZs configuration has been successfully imported.
                            </div>
                        </>
                    }

                    {hasErrors &&
                        <>
                            <div>
                                Import was unsuccessful.
                                {importErrors.length > 0 &&
                                    <> Here is the list of the errors.</>
                                }
                            </div>

                            {importErrors.length > 0 &&
                                <Table
                                    testId={"import-errors-table"}
                                    hideCheckbox={true}
                                    columns={columns}
                                    rows={importErrors}
                                    compact={true}
                                    requestParams={{}}
                                    hidePaging={true}
                                />
                            }
                        </>
                    }
                    {isImportSuccessful &&
                        <div className={styles["imported-info"]}>
                            To see the list of imported entities please click &nbsp;
                            <LinkButton
                                testId="conflicting-entities-link"
                                onClick={() => {
                                    setSelectedImportValidationStatusItem(props.importResult as IImportValidationSuccessStatus);
                                    setIsEntityComparisonDialogOpen(true);
                                }}
                            >
                                here.
                            </LinkButton>
                        </div>
                    }
                </div>
            </InfoDialog>

            <ImportBzsEntityComparisonDialog
                isOpen={isEntityComparisonDialogOpen}
                importInfo={selectedImportValidationStatusItem}
                onClose={() => {
                    setIsEntityComparisonDialogOpen(false);
                }}
            />
        </>
    );
}
